import { memo, useMemo, useRef, useState } from "react";

// Windowed renderer for multi-megabyte spec text: only pages near the viewport are in the DOM.
// Prevents the 3–4 MB single text node (600 ms+ per layout, heavy for extensions) that froze the review page.
const LINE_PX = 18, PAD_PX = 44, OVERSCAN = 4;
const splitPages = (content) => {
  const parts = (content || "").split(/(?=\[Page \d+\])/);
  return parts.filter((p) => p.length).map((text, i) => ({ i, text, h: Math.max(80, (text.split("\n").length + 1) * LINE_PX + PAD_PX) }));
};

export const DocumentText = memo(function DocumentText({ content }) {
  const pages = useMemo(() => splitPages(content), [content]);
  const offsets = useMemo(() => { const o = [0]; for (const p of pages) o.push(o[o.length - 1] + p.h); return o; }, [pages]);
  const [range, setRange] = useState([0, 12]);
  const ref = useRef(null);
  const onScroll = () => {
    const el = ref.current; if (!el) return;
    const top = el.scrollTop, bottom = top + el.clientHeight;
    let a = 0; while (a < pages.length - 1 && offsets[a + 1] < top) a++;
    let b = a; while (b < pages.length - 1 && offsets[b] < bottom) b++;
    const next = [Math.max(0, a - OVERSCAN), Math.min(pages.length, b + OVERSCAN)];
    if (next[0] !== range[0] || next[1] !== range[1]) setRange(next);
  };
  if (!pages.length) return <div className="flex-1 p-5 text-slate-500 text-xs">Select a document.</div>;
  const total = offsets[pages.length];
  return (
    <div ref={ref} onScroll={onScroll} className="flex-1 overflow-y-auto font-mono text-xs leading-relaxed text-slate-300" data-testid="document-viewer-body" data-pages={pages.length} data-rendered={range[1] - range[0]}>
      <div style={{ height: total, position: "relative" }}>
        {pages.slice(range[0], range[1]).map((p) => (
          <div key={p.i} style={{ position: "absolute", top: offsets[p.i], minHeight: p.h, left: 0, right: 0 }} className="px-5 py-3 whitespace-pre-wrap border-b border-slate-900/60" data-testid={`doc-page-block-${p.i}`}>{p.text}</div>
        ))}
      </div>
    </div>
  );
});
